import { queryOptions } from "@tanstack/react-query"
import { GatedReceiptFailReason } from "ipctg-inflow-types/InFlowEnums"
import {
  GatedReceiptPOSTRequest,
  GatedRegisterSessionPOSTRequest,
} from "ipctg-inflow-types/InFlowGatedAPI"

import api from "@/utilities/api"

import {
  GatedReceiptError,
  GatedRegisterSessionError,
  GatedThemeError,
} from "@/utilities/expectedErrors"
import themeStorage from "@/utilities/ThemeStorage"
import WelcomeTokenStorage from "@/utilities/WelcomeTokenStorage"

import {
  RECEIPT_BASE_KEY,
  REGISTER_BASE_KEY,
  SHARED_BASE_KEY,
  THEME_BASE_KEY,
} from "./keys"

export async function fetchWelcomeToken(args: { qrLookupID: string }) {
  // api.welcome.POST handles storing the token internally and throws on error
  await api.welcome.POST({ qr: args.qrLookupID })

  // Token is already stored by api.welcome.POST, just retrieve it
  const storedToken = WelcomeTokenStorage.getWelcomeToken()
  if (!storedToken) {
    throw new Error("Welcome token was not stored after API call")
  }
  return storedToken
}

export const registerQueryOptions = (args: GatedRegisterSessionPOSTRequest) => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, REGISTER_BASE_KEY, args],
    queryFn: async () => {
      const rsp = await api.gated.registerSession.POST({ ...args })

      if ("failReason" in rsp) {
        throw new GatedRegisterSessionError(rsp.failReason)
      }

      return rsp
    },
    retry: false,
  })
}

export const receiptQueryOptions = (args: GatedReceiptPOSTRequest) => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, RECEIPT_BASE_KEY, args],
    queryFn: async () => {
      const rsp = await api.receipt.POST({ receiptID: args.receiptID })
      if ("failReason" in rsp) {
        throw new GatedReceiptError(rsp.failReason as GatedReceiptFailReason)
      }
      return rsp
    },
    retry: false,
  })
}

export const themeQueryOptions = (args: { qrLookupID: string | null }) => {
  return queryOptions({
    queryKey: [SHARED_BASE_KEY, THEME_BASE_KEY, args.qrLookupID],
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      if (!args.qrLookupID) return null

      const rsp = await api.theme.POST({ qrLookupID: args.qrLookupID })

      if ("failReason" in rsp) {
        throw new GatedThemeError(rsp.failReason)
      }

      themeStorage.setTheme(args.qrLookupID, rsp.theme)

      return rsp.theme
    },
  })
}
